import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { otpService } from '../../services/otpService';
import { setMobileSession } from '../../lib/supabase';

import MobileNumberInput from './components/MobileNumberInput';
import OTPInput from './components/OTPInput';
import ResendOTPTimer from './components/ResendOTPTimer';
import AuthenticationInfoCard from './components/AuthenticationInfoCard';
import SuccessMessage from './components/SuccessMessage';

const MobileAuthentication = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOTP] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [otpId, setOtpId] = useState(null);

  // Removed test credentials for production

  const validateMobile = () => {
    if (!mobile) {
      setErrors({ mobile: 'Mobile number is required' });
      return false;
    }
    if (mobile?.length !== 10) {
      setErrors({ mobile: 'Please enter a valid 10-digit mobile number' });
      return false;
    }
    setErrors({});
    return true;
  };

  const handleRequestOTP = async () => {
    if (!validateMobile()) return;

    setLoading(true);
    try {
      // Generate OTP via MSG91 (via Supabase Edge Function)
      const result = await otpService?.generateOTP(mobile);
      setOtpId(result?.id);
      setStep('otp');
      setErrors({});
      
      // OTP is sent via MSG91 and not returned to frontend for security
      console.log('OTP request sent successfully');
    } catch (err) {
      console.error('Error generating OTP:', err);
      setErrors({ mobile: err?.message || 'Failed to send OTP. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp?.length !== 6) {
      setErrors({ otp: 'Please enter the complete 6-digit OTP' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Verify via Supabase
      await otpService?.verifyOTP(mobile, otp);
      await setMobileSession(mobile);
      localStorage.setItem('authToken', 'verified-jwt-token-' + Date.now());
      localStorage.setItem('userRole', 'coach');
      localStorage.setItem('userMobile', mobile);
      setStep('success');
    } catch (err) {
      console.error('Error verifying OTP:', err);
      
      // Extract and format error message
      let errorMessage = err?.message || 'Invalid OTP. Please try again.';
      
      // Handle Edge Function errors specifically
      if (errorMessage.includes('Edge Function') || errorMessage.includes('non-2xx')) {
        errorMessage = 'Verification service error. Please try again or request a new OTP.';
      }
      
      setErrors({ otp: errorMessage });
      setOTP(''); // Clear OTP on error for easy retry
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOTP('');
    setErrors({});
    setLoading(true);
    
    try {
      // Resend OTP via MSG91 (via Supabase Edge Function)
      const result = await otpService?.resendOTP(mobile);
      setOtpId(result?.id);
      
      // OTP is sent via MSG91 and not returned to frontend for security
      console.log('OTP resend request sent successfully');
    } catch (err) {
      console.error('Error resending OTP:', err);
      setErrors({ otp: err?.message || 'Failed to resend OTP. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessComplete = () => {
    navigate('/registration-dashboard');
  };

  const handleBackToMobile = () => {
    setStep('mobile');
    setOTP('');
    setErrors({});
    setOtpId(null);
  };

  return (
    <>
      <Helmet>
        <title>Mobile Authentication - MI Junior Cricket Registration</title>
        <meta name="description" content="Secure mobile authentication for Mumbai Indians Junior Cricket Tournament registration. Verify your mobile number to register cricket teams." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="container mx-auto px-4 py-8 lg:py-16 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Enhanced Header */}
            <div className="text-center mb-10 lg:mb-16">
              <div className="inline-flex items-center justify-center gap-4 mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-xl"></div>
                  <div className="relative w-20 h-20 lg:w-24 lg:h-24 bg-white rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform duration-300 p-2">
                    <img 
                      src="/assets/images/MIJ-logo.png" 
                      alt="MI Junior Cricket Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="text-left">
                  <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent mb-2">
                    MI Junior
                  </h1>
                  <p className="text-base lg:text-lg text-gray-600 font-medium">
                    Interschool Cricket Tournament Registration 2026
                  </p>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div className="order-2 lg:order-1">
                <AuthenticationInfoCard />
              </div>

              <div className="order-1 lg:order-2">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl p-8 lg:p-10 relative overflow-hidden">
                  {/* Decorative gradient overlay */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-2xl"></div>
                  
                  {step === 'success' ? (
                    <SuccessMessage mobile={mobile} onComplete={handleSuccessComplete} />
                  ) : (
                    <div className="relative z-10">
                      {/* Enhanced Header */}
                      <div className="mb-8">
                        <div className="flex items-center gap-4 mb-3">
                          <div className="w-14 h-14 bg-gradient-to-br from-primary to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                            <Icon name="Smartphone" size={24} color="#FFFFFF" />
                          </div>
                          <div>
                            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
                              {step === 'mobile' ? 'Mobile Verification' : 'Verify OTP'}
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                              {step === 'mobile' 
                                ? 'Enter your mobile number to get started' 
                                : `OTP sent to +91 ${mobile}`}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Simplified LOOPEN SMS Notification */}
                      {step === 'mobile' && (
                        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 border border-blue-200 rounded-xl">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Icon name="MessageSquare" size={20} color="#FFFFFF" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-900 mb-1">
                                SMS from <span className="text-blue-600 font-bold">LOOPEN</span> Short Code
                              </p>
                              <p className="text-xs text-gray-700 leading-relaxed">
                                You will receive OTP and registration status updates via SMS from LOOPEN short code.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 'mobile' ? (
                        <div className="space-y-6">
                          <MobileNumberInput
                            value={mobile}
                            onChange={setMobile}
                            error={errors?.mobile}
                            disabled={loading}
                            onSubmit={handleRequestOTP}
                          />

                          <Button
                            variant="default"
                            size="lg"
                            fullWidth
                            onClick={handleRequestOTP}
                            disabled={loading || mobile?.length !== 10}
                            loading={loading}
                            iconName="Send"
                            iconPosition="right"
                            className="bg-gradient-to-r from-primary to-blue-600 hover:from-blue-700 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                          >
                            {loading ? 'Sending OTP...' : 'Request OTP'}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          <OTPInput
                            value={otp}
                            onChange={setOTP}
                            error={errors?.otp}
                            disabled={loading}
                            onComplete={handleVerifyOTP}
                          />

                          <ResendOTPTimer
                            onResend={handleResendOTP}
                            disabled={loading}
                          />

                          <div className="space-y-3">
                            <Button
                              variant="default"
                              size="lg"
                              fullWidth
                              onClick={handleVerifyOTP}
                              disabled={loading || otp?.length !== 6}
                              loading={loading}
                              iconName="CheckCircle"
                              iconPosition="right"
                              className="bg-gradient-to-r from-primary to-blue-600 hover:from-blue-700 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                            >
                              {loading ? 'Verifying...' : 'Verify OTP'}
                            </Button>

                            <Button
                              variant="ghost"
                              size="default"
                              fullWidth
                              onClick={handleBackToMobile}
                              disabled={loading}
                              iconName="ArrowLeft"
                              iconPosition="left"
                            >
                              Change Mobile Number
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Simplified footer info */}
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="flex items-start gap-3">
                          <Icon name="ShieldCheck" size={18} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-gray-600 leading-relaxed">
                            Your mobile number is used only for authentication and tournament updates. We never share your information with third parties.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Enhanced Footer */}
            <div className="mt-12 text-center space-y-2">
              <p className="text-sm text-gray-600">
                Need help? Contact us at{' '}
                <a href="mailto:mijunior@mumbaiindians.com" className="text-primary hover:text-blue-700 font-semibold hover:underline transition-colors">
                  mijunior@mumbaiindians.com
                </a>
              </p>
              <p className="text-xs text-gray-500">
                <Link to="/terms-and-conditions" className="text-primary hover:text-blue-700 hover:underline transition-colors">
                  Terms and Conditions
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileAuthentication;